<article class="agreement-block text-gray-800 leading-relaxed">
  <header class="text-center mb-8">
    <p class="font-bold text-lg">EXHIBIT B</p>
    <p class="font-bold mt-3">AGREEMENT FOR<br>MARKET DATA DISPLAY SERVICES</p>
    <p class="font-bold mt-2">(Usage-Based Services/Nonprofessional Subscriber Status)</p>
    <p class="font-bold mt-1">(Electronic Version)</p>
  </header>

  <p class="mb-6">
    <strong>{{ vendorName }}</strong> (&ldquo;Vendor&rdquo;) agrees to make &ldquo;Market Data&rdquo;
    available to you pursuant to the terms and conditions set forth in this agreement. By
    executing this Agreement in the space indicated below, you (&ldquo;Subscriber&rdquo;)
    agree to comply with those terms and conditions. Section 1 sets forth terms and
    conditions of general applicability. Section 2 applies insofar as Subscriber receives
    and uses Market Data made available pursuant to this Agreement as a Nonprofessional
    Subscriber.
  </p>

  <h3 class="font-bold text-center mb-4 underline">SECTION 1: TERMS AND CONDITIONS OF GENERAL APPLICABILITY</h3>

  <ol class="list-decimal pl-8 space-y-4 mb-6">
    <li>
      <strong>MARKET DATA DEFINITION</strong> &ndash; For all purposes of this Agreement,
      &ldquo;Market Data&rdquo; means (a) last sale information and quotation information
      relating to securities that are admitted to dealings on the New York Stock Exchange
      (&ldquo;NYSE&rdquo;), (b) such bond and other equity last sale and quotation information,
      and such index and other market information, as United States-registered national
      securities exchanges and national securities associations (each, an &ldquo;Authorizing
      SRO&rdquo;) may make available and as the NYSE may from time to time designate as
      &ldquo;Market Data&rdquo;; and (c) all information that derives from any such information.
    </li>
    <li>
      <strong>PROPRIETARY NATURE OF DATA</strong> &ndash; Subscriber understands and
      acknowledges that each Authorizing SRO and Other Data Disseminator has a proprietary
      interest in the Market Data that originates on or derives from it or its market(s).
    </li>
    <li>
      <strong>ENFORCEMENT</strong> &ndash; Subscriber understands and acknowledges that (a)
      the Authorizing SROs are third-party beneficiaries under this Agreement and (b) the
      Authorizing SROs or their authorized representative(s) may enforce this Agreement, by
      legal proceedings or otherwise, against Subscriber or any person that obtains Market
      Data that is made available pursuant to this Agreement other than as this Agreement
      contemplates. Subscriber shall pay the reasonable attorney&rsquo;s fees that any
      Authorizing SRO incurs in enforcing this Agreement against Subscriber.
    </li>
    <li>
      <strong>DATA NOT GUARANTEED</strong> &ndash; Subscriber understands that no
      Authorizing SRO, no other entity whose information is made available over the
      Authorizing SROs&rsquo; facilities (an &ldquo;Other Data Disseminator&rdquo;) and no
      information processor that assists any Authorizing SRO or Other Data Disseminator in
      making Market Data available (collectively, the &ldquo;Disseminating Parties&rdquo;)
      guarantees the timeliness, sequence, accuracy or completeness of Market Data or of
      other market information or messages disseminated by any Disseminating Party.
      Neither Subscriber nor any other person shall hold any Disseminating Party liable in any
      way for (a) any inaccuracy, error or delay in, or omission of, (i) any such data,
      information or message or (ii) the transmission or delivery of any such data, information
      or message, or (b) any loss or damage arising from or occasioned by (i) any such
      inaccuracy, error, delay or omission, (ii) nonperformance or (iii) interruption in any such
      data, information or message, due either to any negligent act or omission by any
      Disseminating Party, to any &ldquo;force majeure&rdquo; (e.g., flood, extraordinary
      weather conditions, earthquake or other act of God, fire, war, insurrection, riot, labor
      dispute, accident, action of government, communications or power failure, equipment
      or software malfunction) or to any other cause beyond the reasonable control of any
      Disseminating Party.
    </li>
    <li>
      <strong>PERMITTED USE</strong> &ndash; Subscriber shall not furnish Market Data to any
      other person or entity. If Subscriber receives Market Data other than as a
      Nonprofessional Subscriber, it shall use Market Data only for its individual use in its
      business.
    </li>
    <li>
      <strong>DISSEMINATION DISCONTINUANCE OR MODIFICATION</strong> &ndash; Subscriber
      understands and acknowledges that, at any time, the Authorizing SROs may discontinue
      disseminating any category of Market Data, may change or eliminate any transmission
      method and may change transmission speeds or other signal characteristics. The
      Authorizing SROs shall not be liable for any resulting liability, loss or damages that may
      arise therefrom.
    </li>
    <li>
      <strong>DURATION; SURVIVAL</strong> &ndash; This Agreement remains in effect for so
      long as Subscriber has the ability to receive Market Data as contemplated by this
      Agreement. In addition, Vendor may terminate this Agreement at any time, whether at
      the direction of the Authorizing SROs or otherwise. Paragraphs 2, 3 and 4, and the first
      two sentences of Paragraph 8, survive any termination of this Agreement.
    </li>
    <li>
      <strong>MISCELLANEOUS</strong> &ndash; The laws of the State of New York shall govern
      this Agreement and it shall be interpreted in accordance with those laws. This
      Agreement is subject to the Securities Exchange Act of 1934, the rules promulgated
      under that act, and the joint-industry plans entered into pursuant to that act. This
      writing contains the entire agreement between the parties in respect of its subject
      matter. Subscriber may not assign all or any part of this Agreement to any other
      person. The person executing this Agreement below represents and warrants that he or
      she has legal capacity to contract and, if that person is executing this Agreement on
      behalf of a proprietorship or a business, partnership or other organization, represents
      and warrants that he or she has actual authority to bind the organization.
    </li>
  </ol>

  <p class="mb-8 italic">
    ACCEPTED AND AGREED: I, the &ldquo;Subscriber&rdquo; to which the preceding terms and
    conditions refer, acknowledge that I have read the preceding terms and conditions of this
    Section 1, that I understand them and that I hereby manifest my assent to, and my
    agreement to comply with, those terms and conditions by clicking &ldquo;I Agree&rdquo;.
  </p>

  <h3 class="font-bold text-center mb-4 underline">SECTION 2: NONPROFESSIONAL SUBSCRIBER</h3>

  <ol class="list-decimal pl-8 space-y-4 mb-6" start="9">
    <li>
      <strong>NONPROFESSIONAL SUBSCRIBER DEFINITION</strong> &ndash;
      &ldquo;Nonprofessional Subscriber&rdquo; means any natural person who receives market
      data solely for his/her personal, nonbusiness use and who is not a &ldquo;Securities
      Professional.&rdquo; A &ldquo;Securities Professional&rdquo; includes an individual who, if
      working in the United States, is:
      <ol class="list-[lower-alpha] pl-6 mt-3 space-y-2">
        <li>
          registered or qualified with the Securities and Exchange Commission (the
          &ldquo;SEC&rdquo;), the Commodities Futures Trading Commission, any state
          securities agency, any securities exchange or association, or any commodities or
          futures contract market or association.
        </li>
        <li>
          engaged as an &ldquo;investment advisor&rdquo; as that term is defined in Section
          202 (a) (11) of the Investment Advisor&rsquo;s Act of 1940 (whether or not registered
          or qualified under that Act), or
        </li>
        <li>
          employed by a bank or other organization exempt from registration under Federal
          and/or state securities laws to perform functions that would require him or her to be
          so registered or qualified if he or she were to perform such functions for an
          organization not so exempt.
        </li>
      </ol>
      <p class="mt-3">
        A person who works outside of the United States will be considered a
        &ldquo;Securities Professional&rdquo; if he or she performs the same functions as
        someone who would be considered a &ldquo;Securities Professional&rdquo; in the
        United States.
      </p>
      <p class="mt-3">
        Subscriber may not receive Market Data as a &ldquo;Nonprofessional Subscriber&rdquo;
        unless the vendor providing that data to Subscriber first determines that the
        individual falls within Paragraph 9&rsquo;s definition of &ldquo;Nonprofessional
        Subscriber.&rdquo;
      </p>
    </li>
    <li>
      <strong>PERMITTED RECEIPT</strong> &ndash; Subscriber may not receive Market Data
      from Vendor, and Vendor may not provide Market Data to Subscriber, on a
      &ldquo;Nonprofessional Subscriber&rdquo; basis unless Vendor first properly determines
      that Subscriber qualifies as a &ldquo;Nonprofessional Subscriber&rdquo; as defined in
      Paragraph 9 and Subscriber in fact qualifies as a &ldquo;Nonprofessional Subscriber.&rdquo;
    </li>
    <li>
      <strong>PERMITTED USE</strong> &ndash; If Subscriber is a Nonprofessional Subscriber,
      he or she shall receive Market Data solely for his or her personal, non-business use.
    </li>
    <li>
      <strong>PERSONAL AND EMPLOYMENT DATA</strong> &ndash; As a prerequisite to
      qualifying as a &ldquo;Nonprofessional Subscriber&rdquo;, Subscriber shall provide the
      following information:
      <ul class="list-none pl-0 mt-3 space-y-1 text-gray-700">
        <li>Subscriber&rsquo;s name and address:</li>
        <li>Subscriber&rsquo;s occupations:</li>
        <li>Name(s) and address(es) of Subscriber&rsquo;s employer(s):</li>
        <li>Subscriber&rsquo;s title(s) and/or position(s):</li>
        <li>Subscriber&rsquo;s employment functions (description):</li>
      </ul>
      <p class="mt-3">
        In order to facilitate a decision as to whether Subscriber qualifies as a
        &ldquo;Nonprofessional Subscriber,&rdquo; Subscriber shall answer the following
        questions:
      </p>
      <ol class="list-[upper-alpha] pl-6 mt-3 space-y-2">
        <li>Do you use Market Data solely for your personal, non-business use?</li>
        <li>Do you receive Market Data for your business or any other entity?</li>
        <li>Are you currently registered or qualified with the SEC or the CFTC?</li>
        <li>
          Are you currently registered or qualified with any securities agency, any securities
          exchange, association or regulatory body, or any commodities or futures contract
          market, association or regulatory body, in the United States or elsewhere?
        </li>
        <li>
          Whether you are located within or outside of the United States, do you perform
          any functions that are similar to those that require an individual to register or
          qualify with the SEC, the CFTC, any other securities agency or regulatory body, any
          securities exchange or association, or any commodities or futures contract market,
          association or regulatory body?
        </li>
        <li>Are you engaged to provide investment advice to any individual or entity?</li>
        <li>Are you engaged as an asset manager?</li>
        <li>Do you use the capital of any other individual or entity in the conduct of your trading?</li>
        <li>Do you conduct trading for the benefit of a corporation, partnership, or other entity?</li>
        <li>
          Have you entered into any agreement to share the profit of your trading activities
          or receive compensation for your trading activities?
        </li>
        <li>
          Are you receiving office space, and equipment or other benefits in exchange for
          your trading or work as a financial consultant to any person, firm or business
          entity?
        </li>
      </ol>
      <p class="mt-3">
        Subscriber shall notify Vendor promptly in writing of any change in his or her
        circumstances that may cause him or her to cease to qualify as a Nonprofessional
        Subscriber or that may change his or her response to any of the preceding questions.
      </p>
    </li>
    <li>
      <strong>CERTIFICATION</strong> &ndash; By executing this Agreement, Subscriber hereby
      certifies that he or she falls within Paragraph 9&rsquo;s definition of
      &ldquo;Nonprofessional Subscriber&rdquo; and that the personal and employment
      information that he or she has included in Paragraph 12 is truthful and accurate.
    </li>
  </ol>

  <p class="italic mb-2">
    ACCEPTED AND AGREED: I, the &ldquo;Subscriber&rdquo; to which the preceding terms and
    conditions refer, acknowledge that I have read the preceding terms and conditions of this
    Section 2, that I understand them and that I hereby manifest my assent to, and my
    agreement to comply with, those terms and conditions by clicking &ldquo;I Agree&rdquo;.
  </p>

  <p class="text-xs text-gray-500 mt-8">Last Update: March 4, 2013</p>
</article>
